/*
 * Agent Chat "관련 화면" 링크 → 대상 화면 검색조건(viewState) 프리셋.
 *
 * 챗 백엔드(saas-ai-agent/src/chat/screen_links.py)는 **업무 파라미터만** 보낸다
 *   { item_cd, item_hrchy_cd, loc_cd, customer_cd, resource_cd, from_date, to_date, plan_ver_id }.
 * 그것을 각 화면이 마운트 시 reduxUtil.getViewState(viewName) 으로 읽는 **정확한 필드명·형태**로
 * 번역하는 것이 이 파일의 유일한 책임이다. 화면 지식을 챗(components/agent/**)에 넣지 않기
 * 위해 pages/ 쪽에 두고 App.js 만 import 한다.
 *
 * [계약] pages/master-plan/mp-model/floNavActions.js 와 같다 — 그쪽 헤더 주석도 함께 볼 것.
 *   updateViewState 는 같은 viewName 의 기존 상태에 **병합**된다(viewStates.js). 이전 방문에서
 *   남은 version·형제 검색 필드가 그대로 살아 결과를 과도하게 거른다. 그래서 각 payload 는 그
 *   화면이 읽는 필드를 **모두** 지정해 깨끗한 단일 조건으로 연다(App.js 가 removeViewState 를
 *   먼저 부르지만, 병합 의미론에 기대지 않는 편이 안전하다).
 *
 * [버전] version 계열은 채우지 않는다 → 화면 자체의 "최신 버전 자동 선택"이 동작한다.
 *   챗이 특정 버전을 본 턴이라도 그 버전이 리포트 화면에서 선택 가능하다는 보장이 없어,
 *   잘못 심으면 빈 화면이 된다.
 *
 * [페이지] currentPage 뿐 아니라 pageSize 도 함께 지정한다. 다수 화면이
 *   `useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0])` 형태라 viewState 를
 *   심기만 하고 pageSize 를 빼면 undefined 가 된다(삼항의 기준이 필드가 아니라 객체 존재다).
 *
 * [거점] locationSearch 는 `{ locCd, name }` 객체다 — 필터는 locCd, 표시는 name 을 쓴다
 *   (LocationItem.js 의 Autocomplete getOptionLabel/isOptionEqualToValue 근거).
 *   백엔드가 확정한 이름(loc_name 등)이 오면 그것을 표시명으로 쓰고, 없으면 코드를 그대로 쓴다.
 *   Autocomplete 는 코드로 이름을 되찾지 않으므로 이름은 반드시 백엔드가 실어야 한다.
 *   ⚠️ 리포트 화면의 `filter.locations` 트리 필터는 형태가 다르고 검증되지 않아 null(전체)로 둔다.
 *   주의: 거점 검색이 출발·도착 두 칸인 화면도 있다(BOD 계열·출하 거래).
 *      그쪽은 `{ originLocation, destLocation }` 을 넘긴다 —
 *      단일 객체를 주면 화면이 두 칸을 못 찾아 조건이 조용히 빈다.
 *      BOD 계열은 챗이 전개 방향을 알아낸 턴에 dest_loc_cd(이 거점으로 들어오는 경로) 또는
 *      origin_loc_cd(이 거점에서 나가는 경로)를 loc_cd 와 **함께** 보낸다. 방향 축이 이기고,
 *      없으면 도착 칸이 기본이다.
 *
 * [미등록 화면] 프리셋이 없으면 null 을 반환하고 App 은 조건 없이 화면만 연다.
 *   링크 자체를 없애는 것보다 낫고, 백엔드 매핑 확장이 프리셋 작성을 기다리지 않아도 된다.
 */

// 다수 화면의 pageSizes 첫 값이 100 이다(WIP 포함). 위 [페이지] 주석 참조.
const PAGE_RESET = { currentPage: 1, pageSize: 100 };

/*
 * 표시명 — 백엔드가 resolve_entity 로 **확정한 이름**을 함께 보낼 때만 쓰고, 없으면 코드를
 * 그대로 쓴다(이름을 지어내면 사용자가 고르지 않은 대상을 고른 것처럼 보인다).
 * 조회 조건 자체는 어느 쪽이든 코드로 걸리므로, 이름은 순수하게 표시용이다.
 */
const nameOf = (code, name) => name || code;

const locObjOf = (code, name) => (code ? { locCd: code, name: nameOf(code, name) } : null);
const locObj = (p) => locObjOf(p.loc_cd, p.loc_name);
// 거점 검색이 출발·도착 두 칸인 화면용 — 단일 칸 화면과 모양이 다르다(BOD 계열, 출하 거래).
//   단일 객체를 그대로 넘기면 화면이 originLocation/destLocation 을 못 찾아 조건이 조용히 빈다.
//   어느 칸에 싣는지는 화면이 무엇을 기준으로 보는지를 따른다.
const locPairDest = (p) => ({ originLocation: null, destLocation: locObj(p) });
const locPairOrigin = (p) => ({ originLocation: locObj(p), destLocation: null });
// 조달 경로는 방향이 있다 — 챗이 전개 방향을 알아낸 턴은 그 칸에, 모르는 턴은 도착 칸에 싣는다.
//   반대 칸은 null 로 명시한다 — 위 [계약] 대로 화면이 읽는 두 칸을 모두 지정해야 한다.
const bodLocPair = (p) => {
  if (p.origin_loc_cd) {
    return { originLocation: locObjOf(p.origin_loc_cd, p.origin_loc_name), destLocation: null };
  }
  if (p.dest_loc_cd) {
    return { originLocation: null, destLocation: locObjOf(p.dest_loc_cd, p.dest_loc_name) };
  }
  return locPairDest(p);
};
const item = (p) => p.item_cd ?? "";

/*
 * 계획 결과 리포트 화면의 필터 3종 — 트리 필터 컴포넌트가 쓰는 형태 그대로 만든다.
 *   locations / destLocation : [{ locCd, name }]  (LocationTreeFilter)
 *   category                 : [{ hrchyCd, name }] (ItemHrchyTreeFilter)
 *     → 서버에는 categoryCds = category.map(c => c.hrchyCd) 로 나간다(RpReview.js:976).
 *   itemFilterList           : { itemCd: [...] }   (품목 코드 직접 지정)
 * 표시명은 위 nameOf 규약을 따른다 — 확정된 이름이 오면 그것을, 없으면 코드를 쓴다.
 */
const locList = (p) => (p.loc_cd ? [{ locCd: p.loc_cd, name: nameOf(p.loc_cd, p.loc_name) }] : null);
const hrchyList = (p) => (p.item_hrchy_cd
  ? [{ hrchyCd: p.item_hrchy_cd, name: nameOf(p.item_hrchy_cd, p.item_hrchy_name) }] : null);
const itemFilter = (p) => (p.item_cd ? { itemCd: [p.item_cd] } : {});

// 품목 검색을 쓰는 마스터 화면들의 공통 축 — 형제 필드는 각 프리셋이 추가로 비운다.
const itemBase = (p) => ({ ...PAGE_RESET, itemSearch: item(p), extraItemCodes: [], advancedFilters: null });

const PRESETS = {
  // ── 기준정보 · 관계 (챗이 "조회할 수 없습니다"로 안내하는 주제의 목적지) ──
  INPUT_ITEM:        (p) => ({ ...itemBase(p), itemTypeSearch: null }),
  INPUT_BOM:         (p) => ({ ...itemBase(p), itemTypeSearch: null, filter: {} }),
  INPUT_BOM_TREE:    (p) => ({ ...itemBase(p), itemTypeSearch: null, selectedRow: null, expansionType: null }),
  INPUT_PRODUCTION_ROUTE:   (p) => ({ ...itemBase(p), routeSearch: null }),
  INPUT_PRODUCTION_ROUTING: (p) => ({ ...itemBase(p), routeSearch: null, resourceSearch: null }),
  // 조달 경로는 챗이 알아낸 전개 방향을 따른다 — 방향이 없는 턴만 도착 거점이 기준이다.
  INPUT_BOD:         (p) => ({ ...PAGE_RESET, locationSearch: bodLocPair(p), advancedFilters: null }),
  INPUT_BOD_ITEM:    (p) => ({ ...itemBase(p), locationSearch: bodLocPair(p) }),
  INPUT_ALT_SUPPLY:  (p) => ({ ...itemBase(p), locationSearch: locObj(p), routeSearch: null }),
  INPUT_SUPPLIER_ITEM: (p) => ({ ...itemBase(p), locationSearch: locObj(p), supplierSearch: null }),

  // ── 기준정보 · 마스터 ──
  INPUT_LOCATION_ITEM: (p) => ({ ...itemBase(p), locationSearch: locObj(p) }),
  INPUT_CUST_ITEM:   (p) => ({ ...itemBase(p), customerSearch: p.customer_cd ?? "", extraCustomerCodes: [] }),
  INPUT_LOC:         (p) => ({ ...PAGE_RESET, searchCode: p.loc_cd ?? "", locationSearch: null, advancedFilters: null }),
  INPUT_CUS:         (p) => ({ ...PAGE_RESET, searchCode: p.customer_cd ?? "", extraCustomerCodes: [], advancedFilters: null }),
  INPUT_SUPPLIER:    (p) => ({ ...PAGE_RESET, searchCode: "", advancedFilters: null }),
  // 품목 계층 마스터는 계층 코드 한 축으로만 검색한다(ItemHrchyMaster.js:116 이 hierarchyCd 로 건다).
  //   품목 코드 입력칸이 없어 item_cd 만 온 턴은 심을 조건이 없다.
  //   페이지 필드가 없는 화면이라 PAGE_RESET 도 넣지 않는다.
  INPUT_ITEM_HRCHY:  (p) => (p.item_hrchy_cd
    ? { searchCode: p.item_hrchy_cd, advancedFilters: null } : null),
  INPUT_MATERIAL_RECEIPT_PLAN: (p) => ({ ...itemBase(p), locSearch: null, supplierSearch: null }),

  // ── 실적(트랜잭션) — 기간은 챗이 본 구간을 그대로 인계한다(서버가 절대 날짜로 확정해 보낸다) ──
  //   start/end/date 는 top-level ISO 문자열이면 getViewState 가 ZDate 로 되살린다(redux-util.js).
  INPUT_SALES_TXN:   (p) => ({
    ...itemBase(p), locationSearch: locObj(p),
    customerSearch: p.customer_cd ?? "", extraCustomerCodes: [],
    ...(p.from_date && p.to_date ? { start: p.from_date, end: p.to_date } : {}),
  }),
  INPUT_INVENTORY_TXN: (p) => ({
    ...itemBase(p), locationSearch: locObj(p),
    ...(p.to_date ? { date: p.to_date } : {}),
  }),
  // 출하는 방향을 밝히지 않은 질문의 기준이 출발 거점이다(NL2SQL 방향 가드와 같은 규약).
  INPUT_SHIPMENT_TXN: (p) => ({
    ...itemBase(p), locationSearch: locPairOrigin(p),
    customerSearch: p.customer_cd ?? "", extraCustomerCodes: [], selectedSearch: null,
    ...(p.from_date && p.to_date ? { period: { start: p.from_date, end: p.to_date } } : {}),
  }),
  WIP:               (p) => ({ ...itemBase(p), resourceSearch: null, filter: { locations: null } }),

  // ── 계획 결과 리포트 (RP) ──
  //    챗이 "저번 보충계획에서 전자제품군은…" 처럼 좁혀 답한 뒤 여는 화면들이라,
  //    품목 계층(category)·거점 필터를 반드시 옮겨야 같은 범위의 숫자를 본다.
  RP_REVIEW:         (p) => ({
    ...PAGE_RESET, rpVersion: null,
    filter: { originLocation: null, destLocation: locList(p), category: hrchyList(p) },
    // category 가 있으면 서버가 itemFilterList 를 무시한다(RpReview.js:975) — 둘 다 채워도 안전.
    itemFilterList: itemFilter(p), itemFilterType: null,
    itemTypeSearch: null, invClassCdList: null, appliedExceptions: null,
    advancedFilters: null, orderByItem: null, initialFlg: null,
  }),
  RP_ORDER_LIST:     (p) => ({
    ...PAGE_RESET, rpVersion: null,
    filter: { originLocation: null, destLocation: locList(p), category: hrchyList(p) },
    itemSearch: item(p), extraItemCodes: [], period: null, selectedSearch: null,
  }),
  RP_POLICY:         (p) => ({
    ...PAGE_RESET,
    filter: { locations: locList(p), category: hrchyList(p) },
    itemSearch: item(p), extraItemCodes: [], invClassCdList: null,
    period: null, selectedSearch: null, advancedFilters: null,
  }),
  // 충족률 대시보드는 INV_EXC 와 같은 축이다(FillRateDashboard.js:68-71).
  //   페이지 필드가 없는 차트 화면이라 PAGE_RESET 을 넣지 않는다.
  FILL_RATE_DASHBOARD: (p) => ({
    rpVersion: null,
    filter: { locations: locList(p), category: hrchyList(p) },
    itemSearch: item(p), extraItemCodes: [],
  }),
  INV_EXC:           (p) => ({
    rpVersion: null,
    filter: { locations: locList(p), category: hrchyList(p) },
    itemSearch: item(p), extraItemCodes: [],
  }),
  PSI_SIMULATION:    (p) => ({
    ...PAGE_RESET, rpVersion: null,
    filter: { originLocation: null, destLocation: locList(p), category: hrchyList(p) },
    itemFilterList: itemFilter(p), itemFilterType: null,
    invClassCdList: null, appliedExceptions: null,
    advancedFilters: null, orderByItem: null, initialFlg: null, editTarget: null,
  }),

  // ── 계획 결과 리포트 (IP) ──
  ABC_XYZ_RESULTS:   (p) => ({
    ...PAGE_RESET, filter: { locations: locList(p), category: hrchyList(p) },
    itemSearch: item(p), extraItemCodes: [], classification: null,
  }),
  TARGET_INV_SIMULATION_RESULTS: (p) => ({
    ...PAGE_RESET, ipVersion: null,
    filter: { locations: locList(p), category: hrchyList(p) },
    itemSearch: item(p), extraItemCodes: [],
  }),
  INV_OVERVIEW:      (p) => ({
    filter: { locations: locList(p), category: hrchyList(p) },
    itemType: null, period: null, selectedView: null,
  }),

  // ── 계획 결과 리포트 (MP) ──
  MP_PLAN_REVIEW:    (p) => ({
    ...PAGE_RESET, mpVersion: null,
    filter: { locations: locList(p), category: hrchyList(p) },
    itemSearch: item(p), extraItemCodes: [], itemType: null,
    appliedExceptions: null, isChildComp: null,
  }),
  // MP PSI 시뮬레이션 — 같은 이름의 RP PSI 와 축이 다르다.
  //   거점은 filter.locations 이고 품목은 itemSearch 다(MpPsiSimulation.js:80,88).
  //   itemTypeSearch 는 명시적 null 이어야 전체가 된다.
  //   키 자체가 없으면 화면이 완제품(FG) 기본값으로 되돌려 챗이 본 범위보다 좁게 열린다.
  MP_PSI_SIMULATION: (p) => ({
    ...PAGE_RESET, mpVersion: null,
    filter: { locations: locList(p), category: hrchyList(p) },
    itemSearch: item(p), extraItemCodes: [], itemTypeSearch: null,
    appliedExceptions: [], subGridResourceListFilter: [], onlyExceptionApplied: false,
  }),
  MP_PRODUCTION_EXCEPTIONS: (p) => ({
    mpVersion: null, filter: { locations: locList(p) },
    itemSearch: item(p), extraItemCodes: [],
  }),

  // floNavActions.js 에서 payload 형태가 이미 검증된 것들.
  MRP_REPORT:        (p) => ({ ...itemBase(p), version: null, filter: { locations: null } }),
  ORDER_TRACKING:    (p) => ({ ...itemBase(p), mpVersion: null, filter: { locations: null } }),
  RESOURCE_UTILIZATION: (p) => ({
    ...PAGE_RESET,
    resource: p.resource_cd
      ? { resCd: p.resource_cd, name: nameOf(p.resource_cd, p.resource_name) } : null,
    version: null, filter: { locations: null },
  }),
  MP_RESOURCE_GANTT: (p) => ({
    itemCd: item(p), mpVersion: null, resource: [], period: null, ganttData: null,
  }),

  // FLO 차트 — 조회 축이 루트 품목 코드 하나뿐이다.
  //   마운트 경로는 selectedItemCd/rootItemCd 를 읽고(FloChart.js:52-56),
  //   plannel:floSearchItem 수신부는 itemCd 를 읽는다(FloChart.js:91) — 이름이 달라 둘 다 싣는다.
  //   하나만 실으면 닫힌 탭과 열린 탭 중 한쪽이 조용히 빈다.
  //   보기 설정(direction·hideResource 등)은 화면이 스스로 저장하는 값이라 비우지 않는다.
  //   품목이 없으면 심을 조건이 없다 — 빈 코드를 심으면 보고 있던 그래프만 지운다.
  MP_FLO_CHART: (p) => (p.item_cd
    ? { itemCd: p.item_cd, selectedItemCd: p.item_cd, rootItemCd: p.item_cd } : null),
};

/**
 * 링크 파라미터 → 대상 화면 viewState.
 * @param {string} reduxKey 대상 화면의 reduxKey(= viewName)
 * @param {object} params   백엔드가 준 업무 파라미터
 * @returns {object|null}   심을 viewState. 프리셋 미등록이거나 심을 조건이 없으면 null.
 */
export const buildLinkViewState = (reduxKey, params) => {
  const fn = PRESETS[reduxKey];
  if (!fn) return null;
  const p = params || {};
  // 인계할 조건이 하나도 없으면 굳이 심지 않는다 — 빈 조건을 심어도 화면 기본값과 같고,
  //   "조건까지 적용했다"는 안내만 거짓이 된다(App 이 applied 로 문구를 가른다).
  //   ⚠️ 여기 목록에서 축이 빠지면 그 조건만 있는 턴은 프리셋이 있어도 통째로 무시된다
  //      (실사고: item_hrchy_cd 누락 → "전자제품군" 질문의 계층 필터가 전달되지 않음).
  const hasAny = ["item_cd", "item_hrchy_cd", "loc_cd", "customer_cd", "resource_cd",
    "dest_loc_cd", "origin_loc_cd", "from_date", "to_date"]
    .some((k) => p[k] !== undefined && p[k] !== null && p[k] !== "");
  if (!hasAny) return null;
  try {
    return fn(p);
  } catch {
    return null;
  }
};

/**
 * 이미 열려 있는(활성) 탭은 리마운트되지 않아 위 viewState 를 아무도 읽지 않는다.
 * 그 화면이 조회를 다시 태울 수신 리스너를 가진 경우에만 여기에 등록한다.
 *   수신부 선례: MpPsiSimulation.js(plannel:mpPsiSearch), FloChart.js(plannel:floSearchItem)
 * 등록되지 않은 화면은 App 이 applied=false 로 응답하고 챗이 사용자에게 사실대로 안내한다.
 */
export const LINK_REFRESH_EVENTS = {
  INPUT_BOD: "plannel:bodSearch",
  MP_PSI_SIMULATION: "plannel:mpPsiSearch",
  MP_FLO_CHART: "plannel:floSearchItem",
};

const agentScreenLinkPresets = { buildLinkViewState, LINK_REFRESH_EVENTS };
export default agentScreenLinkPresets;
